import { SyntheticEvent, useMemo, useState } from 'react';
import { Box, Grid, Link, Palette, Typography } from '@mui/material';
import styled from '@emotion/styled';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { LanguageObject } from '@/redux/slices/slice_types';
import PolarisIcon from '@/components/_common/PolarisIcon';
import Autocomplete from '../Form/Autocomplete';
import SortableLanguageItem from './SortableLanguageItem';

type LanguageCode = string;

type LanguageSelectorProps = {
  id?: string;
  languages?: LanguageObject[];
  languageCodes?: LanguageCode[];
  color: keyof Palette;
  onChange: (value: LanguageCode[]) => void;
};

type LanguageOption = {
  label: string;
  value: string | null;
  is_short_list?: boolean;
  isMoreOption?: boolean;
};

const normalizeLanguageCode = (code?: string) => (code || '').replace('_', '-').toLowerCase();
const formatLanguageDisplay = (label?: string, code?: string | null) => {
  const normalizedCode = normalizeLanguageCode(code || undefined);
  if (!label || !normalizedCode) return normalizedCode || label || '';
  return `${label} [${normalizedCode}]`;
};

const LanguageSelector = ({ id, languages = [], languageCodes = [], onChange, color = 'primary' }: LanguageSelectorProps) => {
  const allLanguages = languages.map((l) => ({ ...l, value: l.code }));
  const languageOptions = [{ label: '', value: null, is_short_list: true }, ...allLanguages];
  const [expanded, setExpanded] = useState(false);

  const dropdownOptions: LanguageOption[] = useMemo(() => {
    if (!expanded) {
      return [
        ...languageOptions.filter((x) => !languageCodes.find((sl) => sl === x.value) && x.is_short_list),
        { label: 'View All Languages', value: 'expanded', isMoreOption: true }
      ];
    } else {
      return [
        ...languageOptions.filter((x) => !languageCodes.find((sl) => sl === x.value)),
        { label: 'View Default Languages', value: 'expanded', isMoreOption: true }
      ];
    }
  }, [expanded, languageCodes, languages]);

  const onSelectLanguage = (e: SyntheticEvent<Element, Event>, option: any) => {
    if (option.value === 'expanded') {
      setExpanded((e) => !e);
    } else {
      onChange([...languageCodes, option.value]);
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = languageCodes.findIndex((code) => code === active.id);
      const newIndex = languageCodes.findIndex((code) => code === over.id);
      const newOrder = arrayMove(languageCodes, oldIndex, newIndex);
      onChange(newOrder);
    }
  };

  const onRemove = (code: string) => onChange(languageCodes.filter((sl) => sl !== code));

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // This configuration is requeried to make click handler (for Remove button) work properly inside of the sortable item.
        // More info here: https://github.com/clauderic/dnd-kit/issues/591
        distance: 3
      }
    }),
    useSensor(KeyboardSensor)
  );

  return (
    <Grid container spacing={5}>
      <SelectGrid item sm={6}>
        <Autocomplete
          id={id}
          label="Add Languages"
          value={{ label: '', value: null }}
          options={dropdownOptions}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {option.isMoreOption ? <MoreOptionsLink>{option.label}</MoreOptionsLink> : formatLanguageDisplay(option?.label, option?.value) || ''}
            </Box>
          )}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={onSelectLanguage}
          disableCloseOnSelect
          filterSelectedOptions
          disableClearable
          blurOnSelect={false}
        />
        <ArrowPolarisIcon icon="right_double" empty={+(languageCodes.length === 0)} />
      </SelectGrid>

      <Grid item sm={6}>
        {languageCodes.length ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={languageCodes} strategy={verticalListSortingStrategy}>
              {languageCodes.map((code, index) => {
                const language = languages.find((l) => normalizeLanguageCode(l.code) === normalizeLanguageCode(code));

                return (
                  <SortableLanguageItem
                    key={code}
                    index={index}
                    code={code}
                    languageLabel={formatLanguageDisplay(language?.label, code)}
                    color={color}
                    onRemove={onRemove}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        ) : (
          <EmptyTypography variant="subtitle2">No languages selected.</EmptyTypography>
        )}
      </Grid>
    </Grid>
  );
};

export default LanguageSelector;

const MoreOptionsLink = styled(Link)`
  text-transform: uppercase;
  font-size: 0.8rem;
`;

const EmptyTypography = styled(Typography)`
  opacity: 0.5;
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const ArrowPolarisIcon = styled(PolarisIcon)<{ empty: number }>`
  opacity: ${({ empty }) => (empty ? 0.1 : 0.5)};
  font-size: 1.33rem;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const SelectGrid = styled(Grid)`
  display: flex;

  & .MuiAutocomplete-root {
    flex: auto;
    align-self: flex-start;
    padding-right: ${({ theme }) => theme.spacing(4)};
  }
`;
